import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PORTAL, usePortal } from '../portalAuth';
import { STORE_BASE } from '../routeBase';

const STATUS_LABEL = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', rejected: 'Rejected' };
const STATUS_COLOR = { open: '#991b1b', in_progress: '#92400e', resolved: '#166534', closed: '#475569', rejected: '#7f1d1d' };
const TYPE_LABEL = { warranty: 'Warranty', breakage: 'Breakage', wrong_item: 'Wrong Item', missing_item: 'Missing Item', other: 'Other' };

function NewTicketModal({ orders, onClose, onCreated }) {
  const [form, setForm] = useState({ order_id: '', type: 'warranty', subject: '', description: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async e => {
    e.preventDefault();
    if (!form.subject.trim()) return toast.error('Subject is required');
    setSaving(true);
    try {
      await PORTAL.post('/support-tickets', { ...form, order_id: form.order_id || undefined });
      toast.success('Ticket submitted — our team will get back to you shortly');
      onCreated();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not submit ticket'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 26, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>New Support Ticket</h2>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Related Order (optional)</label>
            <select value={form.order_id} onChange={e => set('order_id', e.target.value)} style={inp}>
              <option value="">Not related to a specific order</option>
              {orders.map(o => <option key={o.id} value={o.id}>{o.order_number}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Type</label>
            <select value={form.type} onChange={e => set('type', e.target.value)} style={inp}>
              {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Subject *</label>
            <input value={form.subject} onChange={e => set('subject', e.target.value)} style={inp} required />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={lbl}>Describe the issue</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={4} style={{ ...inp, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="sf-btn-outline" style={btnOutline}>Cancel</button>
            <button type="submit" disabled={saving} className="sf-btn-outline" style={btnPrimary}>{saving ? 'Submitting…' : 'Submit Ticket'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SupportTickets() {
  const { customer, ready } = usePortal();
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    PORTAL.get('/support-tickets').then(r => setTickets(r.data.tickets || [])).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (!customer) return;
    load();
    PORTAL.get('/orders').then(r => setOrders(r.data.orders || [])).catch(() => {});
  }, [customer]);

  if (!ready || loading) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Please sign in to view your support tickets</h2>
      <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)' }}>Sign in</Link>
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '24px auto', padding: '0 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Support Tickets</h1>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>Warranty claims, breakage reports, wrong or missing items, and returns.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="sf-btn-outline" style={btnPrimary}>+ New Ticket</button>
      </div>

      {tickets.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 40, textAlign: 'center', color: '#999' }}>
          No support tickets yet.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden' }}>
          {tickets.map((t, i) => (
            <Link key={t.id} to={`${STORE_BASE}/support-tickets/${t.id}`} style={{
              display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px',
              borderTop: i ? '1px solid #f2f2f2' : 'none', textDecoration: 'none', color: 'inherit',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{t.subject}</div>
                <div style={{ fontSize: 12.5, color: '#888' }}>
                  {t.ticket_number} · {TYPE_LABEL[t.type]}{t.order_number ? ` · Order ${t.order_number}` : ''} · {new Date(t.created_at).toLocaleDateString()}
                </div>
              </div>
              <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 12, background: `${STATUS_COLOR[t.status]}18`, color: STATUS_COLOR[t.status] }}>
                {STATUS_LABEL[t.status]}
              </span>
              <i className="ti ti-chevron-right" style={{ fontSize: 16, color: '#bbb' }} />
            </Link>
          ))}
        </div>
      )}

      {showNew && <NewTicketModal orders={orders} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

const lbl = { display: 'block', fontSize: 12.5, fontWeight: 500, color: '#555', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const btnOutline = { padding: '9px 20px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', background: '#fff' };
const btnPrimary = { padding: '9px 20px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' };
